import { useCallback, useState, useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ClientSideValidationError } from '@atlassian/jira-business-error-handling/src/utils/client-side-validation-error/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	ListIssueForCopyPaste,
	ListIssueFieldTransformed,
	ClipboardItem,
	PasteMetadata,
} from '../../types.tsx';
import { VALIDATION_ERROR_CODES } from '../constants.tsx';
import { type AnyPromise, getFieldValueAsClipboardItem, addToClipboard } from '../utils.tsx';

export function useClipboardHandler<T>({
	issue,
	field,
	currentStringValue,
	currentValue,
	getPastedValue,
	saveValue,
	emptyValue,
}: {
	issue: ListIssueForCopyPaste;
	field: ListIssueFieldTransformed;
	currentStringValue: string | null;
	currentValue: T | null;
	getPastedValue: (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => T | null;
	saveValue: (value: T | null, meta: null, analyticsEvent: UIAnalyticsEvent) => AnyPromise;
	emptyValue?: T;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getContentToCopy = useCallback(
		() => getFieldValueAsClipboardItem<T>(field, issue, currentStringValue, currentValue),
		[currentStringValue, currentValue, field, issue],
	);

	const [contentToCopy, setContentToCopy] = useState<ClipboardItem | null>(getContentToCopy);

	useEffect(() => {
		if (contentToCopy?.html !== getContentToCopy()?.html) {
			setContentToCopy(getContentToCopy());
		}
	}, [getContentToCopy, contentToCopy]);

	const copyHandler = useCallback(async () => {
		const { html, plainText } = getFieldValueAsClipboardItem<T>(
			field,
			issue,
			currentStringValue,
			currentValue,
		);

		if (!plainText || !html) {
			throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NULL_GETTER);
		}

		await addToClipboard(plainText, html);
	}, [currentStringValue, currentValue, field, issue]);

	const pasteHandler = useCallback(
		async (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => {
			const value = getPastedValue(clipboardItem, metadata);

			if (!value || (Array.isArray(value) && !value.length)) {
				throw new ClientSideValidationError(VALIDATION_ERROR_CODES.EMPTY_CLIPBOARD);
			}

			await saveValue(value, null, createAnalyticsEvent({}));
		},
		[getPastedValue, saveValue, createAnalyticsEvent],
	);

	const cutHandler = useCallback(async () => {
		await saveValue(emptyValue ?? null, null, createAnalyticsEvent({}));
	}, [saveValue, emptyValue, createAnalyticsEvent]);

	return {
		contentToCopy,
		copyHandler,
		pasteHandler,
		cutHandler,
	};
}
